import React, { useState, useEffect, useRef } from 'react';
import { bool, func, object, oneOf, shape } from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { apiBaseUrl, post } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';

import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { currentUserListingSelector, sendVerificationEmail } from '../../ducks/user.duck';

import {
  Page,
  Heading,
  NamedRedirect,
  LinkTabNavHorizontal,
  SocialLoginButton,
  ResponsiveBackgroundImageContainer,
  Modal,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';
import EmailVerificationInfo from './EmailVerificationInfo';

// We need to get ToS asset and get it rendered for the modal on this page.
import { TermsOfServiceContent } from '../../containers/TermsOfServicePage/TermsOfServicePage';

// We need to get PrivacyPolicy asset and get it rendered for the modal on this page.
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';

import { TOS_ASSET_NAME, PRIVACY_POLICY_ASSET_NAME } from './AuthenticationPage.duck';

import css from './AuthenticationPage.module.css';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import Icons from '../../components/Icons/Icons';
import {
  getDreamHomePageData,
  getIsFirstStepCompleted,
  getIsPropertyDeveloperPage,
} from '../../util/dreamHomePageHelpers';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { DreamHomePageTab, PageName, RedirectType } from '../../util/enums';
import { getBriefPage, isFinalStepCompletedForBriefs } from '../../util/genericHelpers';

// Social login buttons are needed by AuthenticationForms
export const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const { isLogin, showFacebookLogin, showGoogleLogin, from } = props;
  const showSocialLogins = showFacebookLogin || showGoogleLogin;

  const getDefaultRoutes = () => {
    const baseUrl = apiBaseUrl();

    // Route where the user should be returned after authentication
    // This is used e.g. with EditListingPage and ListingPage
    const fromParam = from ? `from=${from}` : '';

    // Default route where user is returned after successfull authentication
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);
    const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

    // Route for confirming user data before creating a new user
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);
    const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

    return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
  };

  const authWithFacebook = () => {
    const defaultRoutes = getDefaultRoutes();
    const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  const authWithGoogle = () => {
    const defaultRoutes = getDefaultRoutes();
    const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  return showSocialLogins ? (
    <div className={css.idpButtons}>
      {/* <div className={css.socialButtonsOr}>
        <span className={css.socialButtonsOrText}>OR</span>
      </div> */}

      {showFacebookLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithFacebook()}>
            <span className={css.buttonIcon}>{FacebookLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={css.buttonIcon}>{GoogleLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}
    </div>
  ) : null;
};

// Tabs for SignupForm and LoginForm
export const AuthenticationForms = props => {
  const {
    isLogin,
    showFacebookLogin,
    showGoogleLogin,
    from,
    submitLogin,
    loginError,
    signupError,
    authInProgress,
    submitSignup,
    termsAndConditions,
    isFirstStepCompleted,
    isPropertyDeveloperPage,
    history,
    routeConfiguration,
    isSupplierSignup,
    isCustomerSignup,
  } = props;
  const fromState = { state: from ? { from } : null };
  const tabs = [
    {
      text: (
        <Heading as={!isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.signupLinkText" />
        </Heading>
      ),
      selected: !isLogin,
      linkProps: {
        name: 'SignupPage',
        to: fromState,
      },
    },
    {
      text: (
        <Heading as={isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.loginLinkText" />
        </Heading>
      ),
      selected: isLogin,
      linkProps: {
        name: 'LoginPage',
        to: fromState,
      },
    },
  ];

  const handleSubmitSignup = async values => {
    const dreamHomePageData = getDreamHomePageData();
    const listingParams = { ...dreamHomePageData };
    const { fname, lname, ...rest } = values;

    // Map supplier signup user types to core user types
    let mappedUserType = rest.userType;
    if (isSupplierSignup && rest.userType) {
      const supplierTypeMapping = {
        'offsiteSupplier': 'offsiteSupplier',
        'builder': 'offsiteSupplier',
        'architect': 'architectProfessional',
        'professionalServices': 'architectProfessional',
      };
      mappedUserType = supplierTypeMapping[rest.userType] || rest.userType;
    }

    const params = {
      firstName: fname.trim(),
      lastName: lname.trim(),
      isFirstStepCompleted,
      ...rest,
      ...(mappedUserType !== rest.userType ? { userType: mappedUserType } : {}),
    };

    /* OLD: Mail Octopus integration for design alerts
    // attempt mail API, but don't let it block signup
    let designAlertsId;
    try {
      const resp = await post('/api/mail_octopus/user', {
        email: values.email,
        shouldCreateNew: values.subscribedToDesignAlerts === 'yes',
        subscriptionStatus:
          values.subscribedToDesignAlerts === 'yes' ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
        firstName: fname,
        lastName: lname,
      });
      designAlertsId = resp?.id;
    } catch (mailError) {
      console.error('Mail API failed, continuing signup:', mailError);
    }
    const signupParams = designAlertsId ? { ...params, designAlertsId } : params;
    */

    // NEW: Store contact consent in params if given
    // FieldCheckboxGroup creates an array of checked values
    if (values.contactConsentCheckbox && values.contactConsentCheckbox.includes('consentGiven')) {
      params.contactConsentGiven = true;
    }

    // Store supplier signup flag for post-verification redirect
    if (isSupplierSignup) {
      params.isSupplierSignup = true;
    }

    // Store customer signup flag for post-verification redirect
    if (isCustomerSignup) {
      params.isCustomerSignup = true;
    }

    // Attach PP ref from StartYourProject page if present
    try {
      const ppRef = sessionStorage.getItem('startYourProject_ppRef');
      if (ppRef) {
        params.planningReference = ppRef;
        sessionStorage.removeItem('startYourProject_ppRef');
      }
    } catch (e) {}

    // always call submitSignup
    await submitSignup(params, listingParams);

    // and then navigate if needed
    if (isFirstStepCompleted) {
      const selectedPageName = isPropertyDeveloperPage
        ? PageName.PROPERTY_DEVELOPER_PAGE
        : PageName.DREAM_HOME_PAGE;
      history.push(
        createResourceLocatorString(selectedPageName, routeConfiguration, {
          tab: DreamHomePageTab.DESIGN_BRIEF,
        })
      );
    }
  };

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  // eslint-disable-next-line no-confusing-arrow
  const errorMessage = (error, message) => (error ? message : null);
  const loginOrSignupError = isLogin
    ? errorMessage(loginError, loginErrorMessage)
    : errorMessage(signupError, signupErrorMessage);

  return (
    <div className={css.content}>
      {/* <LinkTabNavHorizontal className={css.tabs} tabs={tabs} /> */}
      {isLogin ? (
        <div className={css.authFormHeader}>
          <h1>Log in </h1>
          <p>Welcome back! Please enter your details. </p>
        </div>
      ) : isSupplierSignup ? (
        <div className={css.authFormHeader}>
          <h1>Sign up</h1>
          <p>Start today.</p>
        </div>
      ) : isCustomerSignup ? (
        <div className={css.authFormHeader}>
          <h1>Sign up</h1>
          <p>Start today.</p>
        </div>
      ) : (
        <div className={css.authFormHeader}>
          <h1>Sign up</h1>
          <p>Start today.</p>
        </div>
      )}
      {loginOrSignupError}

      {isLogin ? (
        <LoginForm className={css.loginForm} onSubmit={submitLogin} inProgress={authInProgress} />
      ) : (
        <SignupForm
          className={css.signupForm}
          onSubmit={handleSubmitSignup}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          isSupplierSignup={isSupplierSignup}
          isCustomerSignup={isCustomerSignup}
        />
      )}

      <SocialLoginButtonsMaybe
        isLogin={isLogin}
        showFacebookLogin={showFacebookLogin}
        showGoogleLogin={showGoogleLogin}
        from={from}
      />
    </div>
  );
};

// Form for confirming information from IdP (e.g. Facebook)
// This is shown before new user is created to Marketplace API
const ConfirmIdProviderInfoForm = props => {
  const {
    authInfo,
    authInProgress,
    confirmError,
    submitSingupWithIdp,
    termsAndConditions,
    isFirstStepCompleted,
    history,
    routeConfiguration,
    isPropertyDeveloperPage,
    isSupplierSignup,
    isCustomerSignup,
  } = props;
  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;
  const handleSubmitConfirm = async values => {
    // 1. Pull in your stored data & flags
    const dreamHomePageData = getDreamHomePageData();
    const listingParams = { ...dreamHomePageData, isFirstStepCompleted };

    const { idpToken, email, firstName, lastName, idpId } = authInfo;
    const { email: newEmail, firstName: newFirstName, lastName: newLastName, ...rest } = values;

    // 2. Only send changed fields
    const authParams = {
      ...(newEmail !== email && { email: newEmail }),
      ...(newFirstName !== firstName && { firstName: newFirstName }),
      ...(newLastName !== lastName && { lastName: newLastName }),
    };

    // 3. Anything else goes into publicData
    let publicData = !isEmpty(rest) ? { ...rest } : null;

    // Map supplier signup user types to core user types
    if (isSupplierSignup && publicData?.userType) {
      const supplierTypeMapping = {
        'offsiteSupplier': 'offsiteSupplier',
        'builder': 'offsiteSupplier',
        'architect': 'architectProfessional',
        'professionalServices': 'architectProfessional',
      };
      const mappedUserType = supplierTypeMapping[publicData.userType] || publicData.userType;
      publicData.userType = mappedUserType;
    }

    /* OLD: Mail Octopus integration for design alerts
    // 4. Prepare Mail Octopus payload
    const postData = {
      email: newEmail || email,
      shouldCreateNew: values.subscribedToDesignAlerts === 'yes',
      subscriptionStatus: values.subscribedToDesignAlerts === 'yes' ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
      firstName,
      lastName,
    };

    // 5. Fire-and-forget Mail Octopus, but never blow up the flow
    let designAlertsId;
    try {
      const resp = await post('/api/mail_octopus/user', postData);
      designAlertsId = resp?.id;
    } catch (err) {
      console.error('Mail Octopus error, continuing signup flow:', err);
    }
    */

    // NEW: Add contact consent if given
    // FieldCheckboxGroup creates an array of checked values
    if (values.contactConsentCheckbox && values.contactConsentCheckbox.includes('consentGiven')) {
      if (publicData) {
        publicData.contactConsentGiven = true;
      } else {
        publicData = { contactConsentGiven: true };
      }
    }

    // Add supplier/customer signup flags for post-verification redirect
    if (isSupplierSignup) {
      if (publicData) {
        publicData.isSupplierSignup = true;
      } else {
        publicData = { isSupplierSignup: true };
      }
    }

    if (isCustomerSignup) {
      if (publicData) {
        publicData.isCustomerSignup = true;
      } else {
        publicData = { isCustomerSignup: true };
      }
    }

    // 4. Build the IDP signup payload
    const submitPayload = {
      idpToken,
      idpId,
      firstName,
      lastName,
      ...authParams,
      ...(publicData && {
        publicData,
      }),
      ...(isFirstStepCompleted && {
        privateData: { isFirstStepCompleted },
      }),
    };
    // 7. Always call your signup-with-IDP
    await submitSingupWithIdp(submitPayload, listingParams);

    // 8. Then navigate if needed
    if (isFirstStepCompleted) {
      const selectedPageName = isPropertyDeveloperPage
        ? PageName.PROPERTY_DEVELOPER_PAGE
        : PageName.DREAM_HOME_PAGE;

      history.push(
        createResourceLocatorString(selectedPageName, routeConfiguration, {
          tab: DreamHomePageTab.DESIGN_BRIEF,
        })
      );
    }
  };

  const confirmErrorMessage = confirmError ? (
    <div className={css.error}>
      {isSignupEmailTakenError(confirmError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  ) : null;

  return (
    <div className={css.content}>
      <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
        <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
      </Heading>

      <p className={css.confirmInfoText}>
        <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
      </p>
      {confirmErrorMessage}
      <ConfirmSignupForm
        className={css.form}
        onSubmit={handleSubmitConfirm}
        inProgress={authInProgress}
        termsAndConditions={termsAndConditions}
        authInfo={authInfo}
        idp={idp}
      />
    </div>
  );
};

export const AuthenticationOrConfirmInfoForm = props => {
  const {
    tab,
    authInfo,
    from,
    showFacebookLogin,
    showGoogleLogin,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    authInProgress,
    loginError,
    signupError,
    confirmError,
    termsAndConditions,
    isFirstStepCompleted,
    isPropertyDeveloperPage,
    isSupplierSignup,
    isCustomerSignup,
  } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';

  return isConfirm ? (
    <ConfirmIdProviderInfoForm
      authInfo={authInfo}
      submitSingupWithIdp={submitSingupWithIdp}
      authInProgress={authInProgress}
      confirmError={confirmError}
      termsAndConditions={termsAndConditions}
      isFirstStepCompleted={isFirstStepCompleted}
      isPropertyDeveloperPage={isPropertyDeveloperPage}
      isSupplierSignup={isSupplierSignup}
      isCustomerSignup={isCustomerSignup}
      history={history}
      routeConfiguration={routeConfiguration}
    />
  ) : (
    <AuthenticationForms
      isLogin={isLogin}
      showFacebookLogin={showFacebookLogin}
      showGoogleLogin={showGoogleLogin}
      from={from}
      loginError={loginError}
      signupError={signupError}
      submitLogin={submitLogin}
      authInProgress={authInProgress}
      submitSignup={submitSignup}
      termsAndConditions={termsAndConditions}
      isFirstStepCompleted={isFirstStepCompleted}
      isPropertyDeveloperPage={isPropertyDeveloperPage}
      isSupplierSignup={isSupplierSignup}
      isCustomerSignup={isCustomerSignup}
      history={history}
      routeConfiguration={routeConfiguration}
    ></AuthenticationForms>
  );
};

const getAuthInfoFromCookies = () => {
  return Cookies.get('st-authinfo')
    ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
    : null;
};
const getAuthErrorFromCookies = () => {
  return Cookies.get('st-autherror')
    ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
    : null;
};

export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(getAuthErrorFromCookies());
  const config = useConfiguration();
  const currentUserListing = useSelector(currentUserListingSelector);
  const [redirectConfig, setRedirectConfig] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Ref to track if redirect has been processed (prevents infinite loop)
  const redirectProcessedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    if (authError) {
      Cookies.remove('st-autherror');
    }
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  const {
    authInProgress,
    currentUser,
    intl,
    isAuthenticated,
    location,
    loginError,
    scrollingDisabled,
    signupError,
    submitLogin,
    submitSignup,
    confirmError,
    submitSingupWithIdp,
    tab,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onManageDisableScrolling,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
    isSupplierSignup,
    isCustomerSignup,
  } = props;

  const isFirstStepCompleted = getIsFirstStepCompleted();
  const isPropertyDeveloperPage = getIsPropertyDeveloperPage();
  const dreamHomePageData = getDreamHomePageData();
  const isFinalStepCompleted = isFinalStepCompletedForBriefs(currentUserListing);
  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  const from = locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : null;

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isLogin = tab === 'login';

  // We only want to show the email verification dialog in the signup
  // tab if the user isn't being redirected somewhere else
  // (i.e. `from` is present). We must also check the `emailVerified`
  // flag only when the current user is fully loaded.
  const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

  // Already authenticated, redirect away from auth page
  useEffect(() => {
    console.log('[AuthPage] ========== useEffect START ==========');
    console.log('[AuthPage] Current URL:', window.location.href);

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const autoRedirectToLeadFlow = urlParams.get('autoRedirectToLeadFlow') === 'true';
    const returnUrl = urlParams.get('returnUrl'); // Check for returnUrl parameter from provisioning email

    console.log('[AuthPage] URL params:', {
      autoRedirectToLeadFlow,
      returnUrl,
      fullUrl: window.location.href
    });

    // Check if this is a LeadFlow return URL (provisioning email)
    const isLeadFlowReturnUrl = returnUrl && (
      returnUrl.includes('leadflow') ||
      returnUrl.includes('leadflowqa') ||
      returnUrl.includes('5173') // Local dev port
    );

    console.log('[AuthPage] isLeadFlowReturnUrl:', isLeadFlowReturnUrl);

    console.log('[AuthPage] Auto-redirect checks:', {
      isAuthenticated,
      currentUserLoaded,
      autoRedirectToLeadFlow,
      isLeadFlowReturnUrl,
      returnUrl,
      shouldAutoRedirect: isAuthenticated && currentUserLoaded && (autoRedirectToLeadFlow || isLeadFlowReturnUrl)
    });

    // Prevent infinite loop - only process redirect once
    // EXCEPTION: If autoRedirectToLeadFlow or isLeadFlowReturnUrl is true, allow processing even if already processed
    // This handles the case where user clicks email link while already logged in another tab
    if (redirectProcessedRef.current && !autoRedirectToLeadFlow && !isLeadFlowReturnUrl) {
      console.log('[AuthPage] Redirect already processed, skipping');
      return;
    }

    console.log('[AuthPage] Proceeding with redirect logic...');

    let cfg = null;

    if (currentUserListing?.id?.uuid && !isFinalStepCompleted) {
      const { page, params } = getBriefPage(currentUserListing);
      cfg = { type: RedirectType.NAMED, name: page, params };
    } else if (isAuthenticated && currentUserLoaded && (autoRedirectToLeadFlow || isLeadFlowReturnUrl)) {
        // CRITICAL: Mark as processed IMMEDIATELY to prevent duplicate execution
        if (redirectProcessedRef.current) {
            console.log('[AuthPage] Already processed LeadFlow auto-open, skipping duplicate');
            return;
        }
        redirectProcessedRef.current = true;
      // Auto-redirect to LeadFlow: Generate SSO token and redirect
      // BUT NOT if user just logged out (isLogout=true) OR has explicit logout flag in sessionStorage
      const handleAutoRedirect = async () => {
        try {
          console.log('[AuthPage] Auto-redirecting to LeadFlow after login...');

          // Check if user has LeadFlow access
          const isLeadflowAccessProvided = user?.attributes?.profile?.publicData?.isLeadflowAccessProvided;

          if (!isLeadflowAccessProvided) {
            console.warn('[AuthPage] User does not have LeadFlow access, redirecting to home');
            cfg = { type: RedirectType.NAMED, name: 'LandingPage', params: {} };
            setRedirectConfig(cfg);
            redirectTimerRef.current = setTimeout(() => setShouldRedirect(true), 1000);
            return;
          }

          // Generate SSO token
          const apiUrl = `${apiBaseUrl()}/api/leadflow/generate-sso-token`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ returnUrl: returnUrl || undefined }),
          });

          if (!response.ok) {
            console.error('[AuthPage] Failed to generate SSO token');
            cfg = { type: RedirectType.NAMED, name: 'LandingPage', params: {} };
            setRedirectConfig(cfg);
            redirectTimerRef.current = setTimeout(() => setShouldRedirect(true), 1000);
            return;
          }

          const data = await response.json();
          if (data.success && data.data.redirectUrl) {
            console.log('[AuthPage] ✅ Redirecting to LeadFlow:', data.data.redirectUrl);

            // Redirect the current window directly. window.open('_blank') was used
            // previously, but since this runs inside an async callback (not a direct
            // user click), browsers treat it as an untrusted popup and silently block
            // it — leaving the user stranded on the Sharetribe landing page.
            window.location.href = data.data.redirectUrl;
          }
        } catch (error) {
          console.error('[AuthPage] Error during auto-redirect to LeadFlow:', error);
          cfg = { type: RedirectType.NAMED, name: 'LandingPage', params: {} };
          setRedirectConfig(cfg);
          redirectTimerRef.current = setTimeout(() => setShouldRedirect(true), 1000);
          return;
        }
      };

      handleAutoRedirect();
      return; // Don't set redirect config for normal flow
    } else if (isAuthenticated && from && !showEmailVerification) {
      console.log('[AuthPage] Redirecting to from:', from);
      cfg = { type: RedirectType.URL, to: from };
    } else if (isAuthenticated && currentUserLoaded && !showEmailVerification) {
      console.log('[AuthPage] Redirecting to LandingPage');
      cfg = { type: RedirectType.NAMED, name: 'LandingPage', params: {} };
    }

    if (cfg) {
      console.log('[AuthPage] Setting redirect config:', cfg);
      redirectProcessedRef.current = true;
      setRedirectConfig(cfg);

      // Store timer in ref so it won't be cleared by subsequent effect runs
      redirectTimerRef.current = setTimeout(() => {
        console.log('[AuthPage] Timer fired, setting shouldRedirect to true');
        setShouldRedirect(true);
      }, 1000);
    } else {
      console.log('[AuthPage] No redirect config set - waiting for conditions');
    }

    // Cleanup function - only clear timer if we're unmounting
    return () => {
      if (redirectTimerRef.current && !redirectProcessedRef.current) {
        console.log('[AuthPage] Cleanup - clearing timer (component unmounting)');
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, [
    currentUserListing,
    isFinalStepCompleted,
    isAuthenticated,
    from,
    currentUserLoaded,
    showEmailVerification,
    isSupplierSignup,
    // Note: 'user' intentionally NOT in dependencies to prevent infinite loop
    // user object is accessed inside but we rely on currentUserLoaded flag instead
  ]);
  // Once the 1 s timer has fired, do the actual redirect
  if (shouldRedirect && redirectConfig) {
    if (redirectConfig.type === RedirectType.NAMED) {
      return <NamedRedirect name={redirectConfig.name} params={redirectConfig.params} />;
    } else {
      return <Redirect to={redirectConfig.to} />;
    }
  }
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';
  const resendErrorMessage = sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });

  const topbarClasses = classNames({
    [css.hideOnMobile]: showEmailVerification,
  });

  // Debug log to check if isSupplierSignup prop is being received

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer isAuthenticationPage={true} className={topbarClasses} />}
        footer={<FooterContainer />}
        isAuthenticationPage={true}
      >
        <div className={css.authContentWrapper}>
          <div className={css.authContentImg}>
            <div className={css.authContentImgDetails}>
              <Icons name="stars" />
              {isSupplierSignup === true ? (
                <>
                  <h1>
                    Join the UK's <span>Fastest-Growing</span> Construction Marketplace
                  </h1>
                  <p>
                    Showcase your work to self-builders and developers — upload your first 5 case studies completely free.
                  </p>
                </>
              ) : isCustomerSignup === true ? (
                <>
                  <h1>
                    Start Your Build With <span>Confidence</span>
                  </h1>
                  <p>
                    Create a free profile and complete your project brief so we can match you with some builders and suppliers
                  </p>
                </>
              ) : (
                <>
                  <h1>
                    {/* Join <span>15,000+</span> people who will self-build in the UK over the next 12
                    months */}
                    See Projects Up to <span>8</span> Weeks Before They Go Public
                  </h1>
                  <p>
                    {/* Create a free account and get full access to a huge network of offsite construction
                    suppliers. No credit card needed.{' '} */}
                    Get planning data earlier than competitors. Try it free for 7 days.
                  </p>
                </>
              )}
            </div>
          </div>
          <div className={css.authContentInfo}>
            {showEmailVerification ? (
              <EmailVerificationInfo
                name={user.attributes.profile.firstName}
                email={<span className={css.email}>{user.attributes.email}</span>}
                onResendVerificationEmail={onResendVerificationEmail}
                resendErrorMessage={resendErrorMessage}
                sendVerificationEmailInProgress={sendVerificationEmailInProgress}
                from={from}
              />
            ) : (
              <AuthenticationOrConfirmInfoForm
                tab={tab}
                authInfo={authInfo}
                from={from}
                showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
                showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
                submitLogin={submitLogin}
                submitSignup={submitSignup}
                submitSingupWithIdp={submitSingupWithIdp}
                authInProgress={authInProgress}
                loginError={loginError}
                signupError={signupError}
                confirmError={confirmError}
                termsAndConditions={
                  <TermsAndConditions
                    onOpenTermsOfService={() => setTosModalOpen(true)}
                    onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                    intl={intl}
                  />
                }
                isFirstStepCompleted={isFirstStepCompleted}
                isPropertyDeveloperPage={isPropertyDeveloperPage}
                isSupplierSignup={isSupplierSignup}
                isCustomerSignup={isCustomerSignup}
              />
            )}
          </div>
        </div>
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
    </Page>
  );
};

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  isSupplierSignup: false,
  isCustomerSignup: false,
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
  privacyAssetsData: null,
  privacyFetchInProgress: false,
  privacyFetchError: null,
  tosAssetsData: null,
  tosFetchInProgress: false,
  tosFetchError: null,
};

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // to fetch privacy-policy page asset
  // which is shown in modal
  privacyAssetsData: object,
  privacyFetchInProgress: bool,
  privacyFetchError: propTypes.error,

  // to fetch terms-of-service page asset
  // which is shown in modal
  tosAssetsData: object,
  tosFetchInProgress: bool,
  tosFetchError: propTypes.error,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    pageAssetsData: privacyAssetsData,
    inProgress: privacyFetchInProgress,
    error: privacyFetchError,
  } = state.hostedAssets || {};
  const { pageAssetsData: tosAssetsData, inProgress: tosFetchInProgress, error: tosFetchError } =
    state.hostedAssets || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: (params, listingParams) => dispatch(signup(params, listingParams)),
  submitSingupWithIdp: (params, listingParams) => dispatch(signupWithIdp(params, listingParams)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
